"use client";

import { LoadingIcon } from "@App/Components/common/Icons";
import {
  AUTOCOMPLETE_MAP,
  AutocompleteSemantic,
} from "@App/domain/autocomplete/autocompleteSemantic";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Field,
} from "@headlessui/react";
import type { FunctionComponent, ReactElement } from "react";
import { useTranslation } from "react-i18next";

export interface Suggestion {
  id: string | null;
  name: string;
  icon?: ReactElement | null;
}

type AutocompleteFieldProps = {
  query: string;
  setQuery: (value: string) => void;
  suggestions: Suggestion[] | null;
  selectedSuggestion: Suggestion | null;
  setSelectedSuggestion: (suggestion: Suggestion | null) => void;
  rounded?: "left" | "right" | "both";
  loading?: boolean;
  placeholder?: string;
  autoComplete?: AutocompleteSemantic;
};

export const AutocompleteField: FunctionComponent<AutocompleteFieldProps> = ({
  query,
  setQuery,
  suggestions,
  selectedSuggestion,
  setSelectedSuggestion,
  rounded = "both",
  loading,
  placeholder,
  autoComplete = AutocompleteSemantic.OFF,
}) => {
  const { t } = useTranslation();

  const autoCompleteValue = AUTOCOMPLETE_MAP[autoComplete];

  return (
    <Field>
      <Combobox
        as="div"
        value={selectedSuggestion ?? { id: null, name: query }}
        onChange={(suggestion: Suggestion) => {
          setQuery("");
          setSelectedSuggestion(suggestion);
        }}
      >
        <div className="relative">
          <ComboboxInput
            placeholder={placeholder}
            name={autoComplete !== AutocompleteSemantic.OFF ? autoCompleteValue : "city-input"}
            autoComplete={autoComplete}
            disabled={loading}
            className={`block w-full ${
              rounded === "both" ? "rounded-md" : ""
            } ${rounded === "left" ? "rounded-l-md" : ""} ${
              rounded === "right" ? "rounded-r-md" : ""
            } bg-white py-2 px-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/7 disabled:cursor-not-allowed`}
            onChange={(event) => setQuery(event.target.value)}
            onBlur={() =>
              !selectedSuggestion &&
              suggestions &&
              suggestions.length > 0 &&
              setSelectedSuggestion(suggestions[0])
            }
            displayValue={(suggestion: Suggestion) => suggestion?.name}
          />
          {loading && (
            <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5" disabled>
              <LoadingIcon className="animate-spin size-4" />
            </ComboboxButton>
          )}
          <ComboboxOptions
            transition
            className="absolute z-10 max-h-60 w-full overflow-auto rounded-md bg-white py-2 text-base shadow-lg outline outline-1 outline-black/5 data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
          >
            {query.length > 0 && !suggestions && (
              <ComboboxOption
                value={{ id: null, name: query }}
                disabled
                className="cursor-default select-none group flex items-center gap-2 rounded-lg px-3 py-1.5"
              >
                <LoadingIcon className="animate-spin size-4" />
                <span className="block truncate">
                  {t("molecules.common.autocomplete.loading", {
                    query,
                  })}
                </span>
              </ComboboxOption>
            )}
            {suggestions && suggestions.length === 0 && (
              <ComboboxOption
                value={{ id: null, name: query }}
                disabled
                className="cursor-default select-none px-3 py-2 text-gray-900"
              >
                {t("molecules.common.autocomplete.noResults")}
              </ComboboxOption>
            )}
            {suggestions?.map((suggestion: Suggestion) => (
              <ComboboxOption
                key={suggestion.id}
                value={suggestion}
                className="text-gray-900 group flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 select-none data-focus:bg-white/10"
              >
                {suggestion.icon}
                <div className="text-sm/6 truncate">{suggestion.name}</div>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </div>
      </Combobox>
    </Field>
  );
};
